import React, { useEffect } from "react";
import { BaseButton, BaseText, BaseToolTip } from "../../../../components/ui";
import SupplyPortal from "../../../../components/ui/SupplyPortal";
import { getFontAwesomeIcon } from "../../../../utils";
import BaseRadioButton from "../../../../components/ui/BaseRadioButton";
import { GroupModalProps } from "../../types";
import { getTableViewGroupsByTableViewSlug } from "../../../../api/toga";

const GroupModal: React.FC<GroupModalProps> = ({
	isOpen,
	onClose,
	groupOptions,
	selectedValue,
	onGroupChange,
	onApply,
	viewSlug,
}) => {
	if (!isOpen) return null;

	// TODO: Commenting this for potential performance issues
	useEffect(() => {
		const fetchTableViewGroupData = async () => {
			const tableViewGroupData = await getTableViewGroupsByTableViewSlug(
				viewSlug
			);
		};
		fetchTableViewGroupData();
	}, [viewSlug]);

	return (
		<SupplyPortal containerId="portal-root">
			<div
				onClick={onClose}
				className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
			>
				<div
					onClick={(e) => e.stopPropagation()}
					className="w-full max-w-xl rounded-lg bg-white p-6 shadow-lg"
				>
					{/* header */}
					<div className="flex items-center justify-between mb-5">
						<div className="flex items-center">
							<span className="text-xs">
								{getFontAwesomeIcon("layerGroup", "solid")}
							</span>
							<BaseText
								text="Group by"
								additionalClasses="pl-2"
								size="text-xl"
								color="text-navy-500"
								tag="h1"
								fontFamily="font-omnes-medium"
							/>
						</div>
						<button
							onClick={onClose}
							className="text-navy-300 hover:text-gray-700"
						>
							<BaseToolTip
								message="Close"
								toolTipPlacement="bottom"
							>
								{getFontAwesomeIcon("xmark", "regular")}
							</BaseToolTip>
						</button>
					</div>

					{/* options list */}
					<div className="space-y-5">
						{groupOptions.map((option) => (
							<div
								key={option.uuid}
								className="flex items-center justify-between"
							>
								<div className="flex items-center space-x-3 ">
									<BaseRadioButton
										name="grouping"
										isChecked={
											selectedValue === option.uuid
										}
										onChange={() =>
											onGroupChange(option.uuid)
										}
										label={""}
									/>
									<span className="font-medium text-sm text-navy-500">
										{option.type}
									</span>

									{/* primary badge */}
									<span
										className={`flex items-center space-x-1 rounded px-2 py-1 text-sm ${
											option.groupModalOptions
												.primaryOption.badgeColor ||
											"bg-supply-blue-50"
										}`}
									>
										<span
											className={`${option.groupModalOptions.primaryOption.textColor}  text-xs`}
										>
											{getFontAwesomeIcon(
												option.groupModalOptions
													.primaryOption.icon,
												(option.groupModalOptions
													.primaryOption
													.weight as "regular") ||
													"solid"
											)}
										</span>
										<span
											className={`${option.groupModalOptions.primaryOption.textColor} text-sm`}
										>
											{
												option.groupModalOptions
													.primaryOption.text
											}
										</span>
									</span>

									{/* optional “by” + secondary badge */}
									{option.groupModalOptions
										.secondaryOption && (
										<>
											<span className="text-sm">by</span>
											<span
												className={`flex items-center space-x-1 rounded px-2 py-1 text-sm ${
													option.groupModalOptions
														.secondaryOption
														.badgeColor ||
													"bg-supply-blue-50"
												}`}
											>
												<span
													className={`${option.groupModalOptions.secondaryOption.textColor} text-xs`}
												>
													{getFontAwesomeIcon(
														option.groupModalOptions
															.secondaryOption
															.icon,
														(option
															.groupModalOptions
															.secondaryOption
															.weight as "regular") ||
															"regular"
													)}
												</span>
												<span
													className={`${option.groupModalOptions.secondaryOption.textColor} text-sm`}
												>
													{
														option.groupModalOptions
															.secondaryOption
															.text
													}
												</span>
											</span>
										</>
									)}
								</div>

								{/* right side: default & menu */}
								<div className="flex items-center space-x-2">
									{option.isDefault && (
										<span className="flex items-center space-x-1 rounded bg-mint-50 px-2 py-1 text-xs font-medium text-mint-800">
											<span>Default</span>
											<span>
												{getFontAwesomeIcon(
													"circleCheck",
													"regular"
												)}
											</span>
										</span>
									)}
									{/* {opt.showMenu && (
										<button className="p-1 hover:bg-gray-100 rounded-full">
											{getFontAwesomeIcon(
												"ellipsis",
												"solid"
											)}
										</button>
									)} */}
								</div>
							</div>
						))}
					</div>

					{/* footer buttons */}
					<div className="mt-6 flex justify-end space-x-3 border-t-1 border-navy-200 pt-5">
						<BaseButton
							text="Cancel"
							onClick={onClose}
							bgColor="bg-transparent"
							fontColor="text-navy-400"
							buttonSize="h-26px"
							shape="rounded-full"
							fontFamily="font-omnes-medium"
							additionalClasses="border border-navy-400 px-4 w-32 hover:bg-navy-100 text-sm"
						/>
						<BaseButton
							text="Apply Grouping"
							onClick={() => {
								const selectedOption = groupOptions.find(
									(option) => option.uuid === selectedValue
								);
								if (selectedOption) {
									onApply(
										selectedOption.tableSlugs
											.primaryTableSlug
									);
								}
							}}
							bgColor="bg-primary"
							fontColor="text-white"
							buttonSize="h-26px"
							shape="rounded-full"
							fontFamily="font-omnes-medium"
							additionalClasses="px-2 w-32 text-sm hover:bg-supply-blue-600"
						/>
					</div>
				</div>
			</div>
		</SupplyPortal>
	);
};

export default GroupModal;
